'use client';

import type {
  CreateBackupZeroEtlPolicyInput,
  UpdateBackupZeroEtlPolicyInput,
  BackupZeroEtlPolicy,
} from '@repo/api-gw-sdk';
import { BackupZeroEtlRoutingMode } from '@repo/api-gw-sdk';

import { getResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { Wizard } from '@/components/wizard/Wizard';
import {
  createAllIncludedCondition,
  createAllExcludedCondition,
} from '@/types/advanceFilter';

import {
  EditZeroEtlPolicyFlow,
  type ZeroEtlPolicyEditState,
} from './ZeroEtlPolicyCreationFlow';

interface ZeroEtlPolicySidePanelProps {
  entity?: BackupZeroEtlPolicy;
  onSave: (
    input: CreateBackupZeroEtlPolicyInput | UpdateBackupZeroEtlPolicyInput,
    policyId?: string
  ) => Promise<void>;
  onAbort: () => void;
}

export function ZeroEtlPolicySidePanel(props: ZeroEtlPolicySidePanelProps) {
  const entity = props.entity;
  const skipFirstStep = !!entity;
  const creatingNew = !entity;
  const policyId = entity?.id || '';

  const save = async (state: ZeroEtlPolicyEditState) => {
    const condition =
      state.resourcesScopeType === 'allIncluded'
        ? createAllIncludedCondition()
        : state.resourcesScopeType === 'allExcluded'
          ? createAllExcludedCondition()
          : state.condition;

    if (!condition) {
      return false;
    }

    const input:
      | CreateBackupZeroEtlPolicyInput
      | UpdateBackupZeroEtlPolicyInput = {
      name: state.name,
      enabled: entity?.enabled ?? true,
      resourceSelector: {
        condition,
        include: state.include ?? [],
        exclude: state.exclude ?? [],
      },
      catalogConfiguration: {
        awsGlue: {
          awsAccountId: state.catalogAwsAccountId,
          awsRegion: state.catalogAwsRegion,
          iamRoleArn: state.catalogIamRoleArn,
        },
      },
      storageConfiguration: {
        vaultId: state.vaultId,
      },
      accessConfiguration: {
        aws: {
          allowedPrincipals: state.allowedPrincipals,
          requirePrincipalTags: state.requirePrincipalTags,
        },
      },
      routingPolicy: {
        routingMode: state.routingMode,
        ...(state.routingMode ===
          BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL && {
          namespaceTemplate: state.namespaceTemplate,
          tableTemplate: state.tableTemplate,
        }),
        ...(state.showAdditionalColumnsSection &&
          state.identityColumns.length > 0 && {
            identityColumns: state.identityColumns,
          }),
      },
    };

    await props.onSave(input, policyId || undefined);
    return true;
  };

  const title = policyId
    ? 'Edit Zero-ETL integration'
    : creatingNew
      ? 'Create Zero-ETL integration'
      : 'Duplicate Zero-ETL integration';

  return (
    <Wizard<ZeroEtlPolicyEditState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      onFinish={save}
      initialStep={{ stepIndex: skipFirstStep ? 1 : 0 }}
      flow={[EditZeroEtlPolicyFlow]}
      initialState={{
        id: policyId,
        name: entity?.name ?? '',
        focusOnName: skipFirstStep && !policyId,
        condition: entity?.resourceSelector?.condition,
        include: entity?.resourceSelector?.include ?? [],
        exclude: entity?.resourceSelector?.exclude ?? [],
        catalogAwsAccountId:
          entity?.catalogConfiguration?.awsGlue?.awsAccountId ?? '',
        catalogAwsRegion:
          entity?.catalogConfiguration?.awsGlue?.awsRegion ?? '',
        catalogIamRoleArn:
          entity?.catalogConfiguration?.awsGlue?.iamRoleArn ?? '',
        vaultId: entity?.storageConfiguration?.vaultId ?? '',
        allowedPrincipals:
          entity?.accessConfiguration?.aws?.allowedPrincipals ?? [],
        requirePrincipalTags:
          entity?.accessConfiguration?.aws?.requirePrincipalTags ?? false,
        resourcesScopeType: getResourcesScopeType(
          entity?.resourceSelector?.condition,
          'useConditions'
        ),
        routingMode:
          entity?.routingPolicy?.routingMode ??
          BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
        namespaceTemplate: entity?.routingPolicy?.namespaceTemplate ?? '',
        tableTemplate: entity?.routingPolicy?.tableTemplate ?? '',
        identityColumns: entity?.routingPolicy?.identityColumns ?? [],
        showAdditionalColumnsSection:
          !!entity?.routingPolicy?.identityColumns?.length,
      }}
      title={title}
      stepperLabels={[
        'Resource selection',
        'Resource list',
        'Table definition',
        'Table access',
      ]}
    />
  );
}
